/**
 * MarkdownRenderer — zero-dependency markdown → MUI component renderer.
 *
 * Parses a controlled subset of markdown (headings, bold, italic, inline code,
 * links, lists, tables, code blocks, blockquotes, thematic breaks) and renders
 * them as MUI Typography / Box / Divider components.
 *
 * Designed for AI-generated summaries where the markdown format is constrained
 * by a system prompt, so full CommonMark compliance is intentionally not needed.
 */

import React from 'react';

// mui
import { alpha, Box, Divider, Typography } from '@mui/material';

// design system
import { COLORS } from '../../../data';

// ─── Typography sizing (matches project standard ≥1.25rem) ──────────────────

const TEXT_BASE = '1.25rem';
const TEXT_LG = '1.5rem';
const TEXT_XL = '1.75rem';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MarkdownRendererProps {
	children: string;
}

// ─── Inline parsing ──────────────────────────────────────────────────────────

/**
 * Parse inline markdown tokens: **bold**, *italic*, `code`, [text](url)
 * Returns an array of React nodes.
 */
const parseInline = (text: string): React.ReactNode[] => {
	// Regex captures (in order of priority):
	//   1. **bold**
	//   2. *italic* (but not **)
	//   3. `inline code`
	//   4. [link text](url)
	//   5. plain text between matches
	const inlineRegex = /(\*\*(.+?)\*\*)|(\*(?!\*)(.+?)\*)|(`(.+?)`)|\[([^\]]+)\]\(([^)]+)\)/g;

	const nodes: React.ReactNode[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;
	let key = 0;

	while ((match = inlineRegex.exec(text)) !== null) {
		// Push any plain text before this match
		if (match.index > lastIndex) {
			nodes.push(text.slice(lastIndex, match.index));
		}

		if (match[1]) {
			// **bold**
			nodes.push(
				<strong key={key++} style={{ fontWeight: 700 }}>
					{match[2]}
				</strong>
			);
		} else if (match[3]) {
			// *italic*
			nodes.push(<em key={key++}>{match[4]}</em>);
		} else if (match[5]) {
			// `inline code`
			nodes.push(
				<Typography
					key={key++}
					component="code"
					sx={{
						bgcolor: '#f3f4f6',
						px: 0.75,
						py: 0.25,
						borderRadius: 1,
						fontSize: TEXT_BASE,
						fontFamily: 'monospace',
					}}>
					{match[6]}
				</Typography>
			);
		} else if (match[7]) {
			// [text](url)
			const linkText = match[7];
			const url = match[8];
			// Only allow http(s) and mailto links; render unsafe links as plain text.
			const safe = /^(https?:|mailto:)/i.test(url);
			if (!safe) {
				nodes.push(linkText);
			} else {
				nodes.push(
					<a
						key={key++}
						href={encodeURI(url)}
						target="_blank"
						rel="noopener noreferrer"
						style={{ color: COLORS.primary.main, textDecoration: 'underline' }}>
						{linkText}
					</a>
				);
			}
		}

		lastIndex = match.index + match[0].length;
	}

	// Push any remaining plain text
	if (lastIndex < text.length) {
		nodes.push(text.slice(lastIndex));
	}

	return nodes.length > 0 ? nodes : [text];
};

// ─── Block parsing ───────────────────────────────────────────────────────────

interface Block {
	type: 'heading' | 'paragraph' | 'ul' | 'ol' | 'code' | 'blockquote' | 'hr' | 'table';
	level?: number; // heading level
	lang?: string; // code block language
	content?: string; // raw text
	items?: string[]; // list items
	rows?: string[][]; // table rows (first = header)
}

const parseBlocks = (markdown: string): Block[] => {
	const lines = markdown.split('\n');
	const blocks: Block[] = [];
	let i = 0;

	while (i < lines.length) {
		const line = lines[i];

		// ── Fenced code block ───────────────────────────────────────────
		if (line.startsWith('```')) {
			const lang = line.slice(3).trim();
			const codeLines: string[] = [];
			i++;
			while (i < lines.length && !lines[i].startsWith('```')) {
				codeLines.push(lines[i]);
				i++;
			}
			blocks.push({ type: 'code', lang, content: codeLines.join('\n') });
			i++; // skip closing ```
			continue;
		}

		// ── Thematic break (---, ***, ___) ──────────────────────────────
		if (/^(\s*[-*_]\s*){3,}$/.test(line)) {
			blocks.push({ type: 'hr' });
			i++;
			continue;
		}

		// ── Heading (# – ###) ───────────────────────────────────────────
		const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
		if (headingMatch) {
			blocks.push({
				type: 'heading',
				level: headingMatch[1].length,
				content: headingMatch[2],
			});
			i++;
			continue;
		}

		// ── Table (| col | col |) ───────────────────────────────────────
		if (/^\|(.+)\|$/.test(line.trim())) {
			const rows: string[][] = [];
			while (i < lines.length && /^\|(.+)\|$/.test(lines[i].trim())) {
				const raw = lines[i].trim();
				// Skip separator rows like |---|---|
				if (/^\|[\s\-:|]+\|$/.test(raw)) {
					i++;
					continue;
				}
				const cells = raw
					.slice(1, -1) // strip leading/trailing |
					.split('|')
					.map((c) => c.trim());
				rows.push(cells);
				i++;
			}
			if (rows.length > 0) {
				blocks.push({ type: 'table', rows });
			}
			continue;
		}

		// ── Blockquote (> ...) ──────────────────────────────────────────
		if (line.startsWith('> ') || line === '>') {
			const quoteLines: string[] = [];
			while (i < lines.length && (lines[i].startsWith('> ') || lines[i] === '>')) {
				quoteLines.push(lines[i].replace(/^>\s?/, ''));
				i++;
			}
			blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
			continue;
		}

		// ── Unordered list (- or * or + prefix) ─────────────────────────
		if (/^[\s]*[-*+]\s+/.test(line)) {
			const items: string[] = [];
			while (i < lines.length && /^[\s]*[-*+]\s+/.test(lines[i])) {
				items.push(lines[i].replace(/^[\s]*[-*+]\s+/, ''));
				i++;
			}
			blocks.push({ type: 'ul', items });
			continue;
		}

		// ── Ordered list (1. prefix) ────────────────────────────────────
		if (/^[\s]*\d+\.\s+/.test(line)) {
			const items: string[] = [];
			while (i < lines.length && /^[\s]*\d+\.\s+/.test(lines[i])) {
				items.push(lines[i].replace(/^[\s]*\d+\.\s+/, ''));
				i++;
			}
			blocks.push({ type: 'ol', items });
			continue;
		}

		// ── Empty line → skip ───────────────────────────────────────────
		if (line.trim() === '') {
			i++;
			continue;
		}

		// ── Paragraph (collect contiguous non-empty, non-special lines) ─
		const paraLines: string[] = [line];
		i++;
		while (
			i < lines.length &&
			lines[i].trim() !== '' &&
			!/^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|\|.+\||(\s*[-*_]\s*){3,}$)/.test(lines[i])
		) {
			paraLines.push(lines[i]);
			i++;
		}
		blocks.push({ type: 'paragraph', content: paraLines.join(' ') });
	}

	return blocks;
};

// ─── Block rendering ─────────────────────────────────────────────────────────

const renderBlock = (block: Block, index: number): React.ReactNode => {
	switch (block.type) {
		case 'heading': {
			const variants: Record<number, { variant: 'h4' | 'h5' | 'h6'; sx: Record<string, unknown> }> =
				{
					1: { variant: 'h4', sx: { fontWeight: 700, mt: 2.5, mb: 1, fontSize: TEXT_XL } },
					2: { variant: 'h5', sx: { fontWeight: 700, mt: 2, mb: 0.75, fontSize: TEXT_LG } },
					3: { variant: 'h6', sx: { fontWeight: 700, mt: 1.5, mb: 0.5, fontSize: TEXT_BASE } },
				};
			const cfg = variants[block.level || 3] || variants[3];
			return (
				<Typography key={index} variant={cfg.variant} sx={cfg.sx}>
					{parseInline(block.content || '')}
				</Typography>
			);
		}

		case 'paragraph':
			return (
				<Typography
					key={index}
					sx={{ mb: 1, lineHeight: 1.7, color: '#374151', fontSize: TEXT_BASE }}>
					{parseInline(block.content || '')}
				</Typography>
			);

		case 'ul':
			return (
				<Box
					key={index}
					component="ul"
					sx={{ pl: 3, mb: 1, '& li': { mb: 0.25 }, fontSize: TEXT_BASE }}>
					{block.items?.map((item, j) => (
						<Typography
							key={j}
							component="li"
							sx={{ lineHeight: 1.7, color: '#374151', fontSize: TEXT_BASE }}>
							{parseInline(item)}
						</Typography>
					))}
				</Box>
			);

		case 'ol':
			return (
				<Box
					key={index}
					component="ol"
					sx={{ pl: 3, mb: 1, '& li': { mb: 0.25 }, fontSize: TEXT_BASE }}>
					{block.items?.map((item, j) => (
						<Typography
							key={j}
							component="li"
							sx={{ lineHeight: 1.7, color: '#374151', fontSize: TEXT_BASE }}>
							{parseInline(item)}
						</Typography>
					))}
				</Box>
			);

		case 'code':
			return (
				<Box
					key={index}
					component="pre"
					sx={{
						bgcolor: '#1f2937',
						color: '#e5e7eb',
						p: 2,
						borderRadius: 2,
						overflow: 'auto',
						mb: 2,
						fontSize: TEXT_BASE,
						fontFamily: 'monospace',
						lineHeight: 1.6,
					}}>
					<code>{block.content}</code>
				</Box>
			);

		case 'blockquote':
			return (
				<Box
					key={index}
					sx={{
						borderLeft: '3px solid',
						borderColor: COLORS.primary.main,
						pl: 2,
						py: 0.5,
						my: 1,
						bgcolor: alpha(COLORS.primary.main, 0.04),
						borderRadius: '0 4px 4px 0',
						fontSize: TEXT_BASE,
					}}>
					{parseInline(block.content || '')}
				</Box>
			);

		case 'hr':
			return <Divider key={index} sx={{ my: 2 }} />;

		case 'table': {
			const rows = block.rows || [];
			if (rows.length === 0) return null;
			const [header, ...body] = rows;
			return (
				<Box
					key={index}
					component="table"
					sx={{
						width: '100%',
						borderCollapse: 'collapse',
						mb: 2,
						'& th, & td': {
							border: '1px solid #e5e7eb',
							px: 1.5,
							py: 0.75,
							fontSize: TEXT_BASE,
							textAlign: 'left',
						},
						'& th': { bgcolor: '#f9fafb', fontWeight: 600 },
					}}>
					<thead>
						<tr>
							{header.map((cell, ci) => (
								<th key={ci}>{parseInline(cell)}</th>
							))}
						</tr>
					</thead>
					{body.length > 0 && (
						<tbody>
							{body.map((row, ri) => (
								<tr key={ri}>
									{row.map((cell, ci) => (
										<td key={ci}>{parseInline(cell)}</td>
									))}
								</tr>
							))}
						</tbody>
					)}
				</Box>
			);
		}

		default:
			return null;
	}
};

// ─── Exported component ──────────────────────────────────────────────────────

/**
 * Renders a markdown string as MUI-styled React components.
 *
 * Usage:
 * ```tsx
 * <MarkdownRenderer>{markdownString}</MarkdownRenderer>
 * ```
 */
export const MarkdownRenderer = ({ children }: MarkdownRendererProps) => {
	if (!children) return null;
	const blocks = parseBlocks(children);
	return <>{blocks.map((block, i) => renderBlock(block, i))}</>;
};

export default MarkdownRenderer;
